import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2, X, Check, Settings, Ruler, Edit2 } from 'lucide-react';
import { useAuth } from './AuthContext';
import { authFetch } from "./api";
import CriteriaMeasurables from './CriteriaMeasurables';

const API = 'https://hs.aida-advisory.com';

const POSITIONS = [
  { group: 'Offense', positions: ['QB', 'RB', 'FB', 'WR', 'TE', 'OT', 'OG', 'C'] },
  { group: 'Defense', positions: ['RE', 'PE', 'DT', 'SAM', 'MIKE', 'WILL', 'CB', 'S'] },
  { group: 'Specialists', positions: ['K', 'P', 'LS', 'KR', 'PR'] },
];

// ── Inline editable text ─────────────────────────────────────────────────────
const InlineEdit = ({ value, onSave, disabled = false }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const inputRef = useRef();

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = () => {
    setEditing(false);
    if (val.trim()) onSave(val.trim());
    else setVal(value);
  };

  if (disabled) return <span>{value}</span>;

  if (!editing) return (
    <span className="cursor-pointer hover:text-[#00D4FF] transition-colors" onClick={() => setEditing(true)}>
      {value}
    </span>
  );

  return (
    <span className="flex items-center gap-1">
      <input ref={inputRef} value={val} onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setVal(value); } }}
        className="bg-[#0A1628] text-white border border-[#00D4FF] rounded px-2 py-1 text-sm focus:outline-none w-full sm:w-40 min-h-[38px]" />
      <button onClick={save} className="text-[#00E676]"><Check size={14} /></button>
      <button onClick={() => { setEditing(false); setVal(value); }} className="text-[#EF5350]"><X size={14} /></button>
    </span>
  );
};

const Criteria = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'super_admin';
  const [selectedPosition, setSelectedPosition] = useState('QB');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [expanded, setExpanded] = useState({});
  const [newSubName, setNewSubName] = useState({});
  const [newCatName, setNewCatName] = useState('');
  const [newCatScope, setNewCatScope] = useState('position');
  const weightTimer = useRef({});

  // ── Fetch criteria for selected position ──────────────────────────────────
  const fetchCriteria = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/api/criteria/hierarchy?position=${selectedPosition}`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
        // Default all collapsed
        setExpanded({});
      }
    } catch (err) {
      console.error('Error fetching criteria:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedPosition]);

  useEffect(() => { fetchCriteria(); }, [fetchCriteria]);

  // ── Update weight ──────────────────────────────────────────────────────────
  const updateWeight = (id, pct) => {
    const num = parseFloat(pct);
    if (isNaN(num) || num < 0 || num > 100) return;
    const decimal = parseFloat((num / 100).toFixed(4));
    setCategories(prev => prev.map(cat => {
      if (cat.id === id) return { ...cat, weight: decimal };
      return {
        ...cat,
        sub_criteria: cat.sub_criteria.map(sc =>
          sc.id === id ? { ...sc, weight: decimal } : sc
        )
      };
    }));
    clearTimeout(weightTimer.current[id]);
    weightTimer.current[id] = setTimeout(async () => {
      setSaveStatus('Saving...');
      try {
        await authFetch(`${API}/api/criteria/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ weight: decimal })
        });
        setSaveStatus('Saved ✓');
        setTimeout(() => setSaveStatus(''), 2000);
      } catch {
        setSaveStatus('Save failed');
      }
    }, 1000);
  };

  // ── Rename criterion ───────────────────────────────────────────────────────
  const renameCriterion = async (id, name) => {
    setSaveStatus('Saving...');
    await authFetch(`${API}/api/criteria/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name })
    });
    setSaveStatus('Saved ✓');
    setTimeout(() => setSaveStatus(''), 2000);
    fetchCriteria();
  };

  // ── Delete criterion ───────────────────────────────────────────────────────
  const deleteCriterion = async (id) => {
    if (!window.confirm('Delete this criterion?')) return;
    try {
      const res = await authFetch(`${API}/api/criteria/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to delete criterion. It may have existing grades linked to it.');
        return;
      }
      fetchCriteria();
    } catch (e) {
      console.error('Delete criterion error:', e);
      alert('Could not reach the server. Please check that the backend is running.');
    }
  };

  // ── Delete category ────────────────────────────────────────────────────────
  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category and all its sub-criteria? Any existing grades for this category will also be removed.')) return;
    try {
      const res = await authFetch(`${API}/api/criteria/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to delete category. It may have existing grades linked to it.');
        return;
      }
      fetchCriteria();
    } catch (e) {
      console.error('Delete category error:', e);
      alert('Could not reach the server. Please check that the backend is running.');
    }
  };

  // ── Add sub-criterion ──────────────────────────────────────────────────────
  const addSubCriterion = async (catId, catPosition) => {
    const name = newSubName[catId]?.trim();
    if (!name) return;
    const position = catPosition || selectedPosition;
    await authFetch(`${API}/api/criteria/sub-criterion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ position, name, description: '', weight: 1.0, parent_id: catId, category_order: 99 })
    });
    setNewSubName(prev => ({ ...prev, [catId]: '' }));
    fetchCriteria();
  };

  // ── Add category ───────────────────────────────────────────────────────────
  const addCategory = async () => {
    if (!newCatName.trim()) return;
    const position = newCatScope === 'all' ? 'ALL' : selectedPosition;
    await authFetch(`${API}/api/criteria/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ position, name: newCatName.trim(), description: '', weight: 1.0 })
    });
    setNewCatName('');
    setNewCatScope('position');
    fetchCriteria();
  };

  const catWeightTotal = Math.round(categories.reduce((sum, cat) => sum + (cat.weight || 0), 0) * 100);
  const catWeightOk = catWeightTotal === 100;

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-6">
      <div className="max-w-4xl mx-auto px-4 md:px-6 pb-12">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 md:mb-6">
          <div>
            <h1 className="text-white text-2xl font-bold">Scouting Criteria</h1>
            <p className="text-[#B0BEC5] text-sm mt-1">Manage evaluation criteria and weights by position</p>
          </div>
          {saveStatus && (
            <span className={`text-sm ${saveStatus === 'Saved ✓' ? 'text-[#00E676]' : 'text-[#FFB300]'}`}>
              {saveStatus}
            </span>
          )}
        </div>

        {/* The criteria are one shared set used by every program, so only the
            platform administrator changes them. */}
        {!canEdit && (
          <div className="panel-card mb-6 text-sm text-[#B0BEC5]">
            These criteria are shared by every program and are maintained by the platform administrator.
            You can view them here; contact AiDA Advisory to request a change.
          </div>
        )}

        {/* Position Selector */}
        <div className="panel-card mb-6">
          {POSITIONS.map(group => (
            <div key={group.group} className="mb-4 last:mb-0">
              <div className="text-[#546E7A] text-xs font-semibold uppercase tracking-wider mb-2">
                {group.group}
              </div>
              <div className="flex flex-wrap gap-2">
                {group.positions.map(pos => (
                  <button
                    key={pos}
                    onClick={() => setSelectedPosition(pos)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      selectedPosition === pos
                        ? 'bg-[#00D4FF]/20 border-[#00D4FF] text-[#00D4FF]'
                        : 'bg-[#243B55]/50 border-[#546E7A]/30 text-[#B0BEC5] hover:border-[#00D4FF]/50 hover:text-white'
                    }`}>
                    {pos}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Category weight total */}
        <div className={`text-xs px-1 mb-3 flex items-center gap-2 ${catWeightOk ? 'text-[#00E676]' : 'text-[#FFB300]'}`}>
          <span>Category weights total: {catWeightTotal}%</span>
          <span>{catWeightOk ? '✓' : '⚠️ Should equal 100%'}</span>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="text-[#00D4FF]">Loading criteria...</div>
          </div>
        )}

        {/* Categories */}
        {!loading && (
          <div className="space-y-3">
            {categories.map(cat => {
              const isOpen = !!expanded[cat.id];
              const subTotal = Math.round(cat.sub_criteria.reduce((sum, sc) => sum + (sc.weight || 0), 0) * 100);
              const subOk = subTotal === 100;

              return (
                <div key={cat.id} className="panel-card p-0 overflow-hidden border border-[#243B55] rounded-xl">

                  {/* Category header */}
                  <div
                    className="flex items-center justify-between gap-2 px-4 py-3 cursor-pointer hover:bg-[#243B55]/40 transition-colors select-none"
                    onClick={() => setExpanded(prev => ({ ...prev, [cat.id]: !prev[cat.id] }))}>

                    <div className="flex items-center gap-3">
                      {isOpen
                        ? <ChevronDown size={16} className="text-[#00D4FF] flex-shrink-0" />
                        : <ChevronRight size={16} className="text-[#546E7A] flex-shrink-0" />}

                      <span onClick={e => e.stopPropagation()}>
                        <InlineEdit value={cat.name} onSave={name => renameCriterion(cat.id, name)} disabled={!canEdit} />
                      </span>

                      {cat.is_universal && (
                        <span className="text-[#546E7A] text-xs italic">universal</span>
                      )}

                      <span className="text-[#546E7A] text-xs">
                        {cat.sub_criteria.length} sub-criteria
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Category weight */}
                      <span onClick={e => e.stopPropagation()} className="flex items-center gap-1">
                        <input
                          type="number"
                          min="0" max="100" step="1"
                          defaultValue={Math.round((cat.weight || 0) * 100)}
                          onChange={e => updateWeight(cat.id, e.target.value)}
                          disabled={!canEdit}
                          className="w-12 bg-[#0A1628] text-[#00D4FF] text-center text-xs rounded border border-[#00D4FF]/30 focus:border-[#00D4FF] focus:outline-none py-0.5"
                        />
                        <span className="text-[#546E7A] text-xs">%</span>
                      </span>

                      {canEdit && !cat.is_universal && (
                        <button
                          onClick={e => { e.stopPropagation(); deleteCategory(cat.id); }}
                          className="text-[#EF5350] hover:text-red-400">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded content */}
                  {isOpen && (
                    <div className="border-t border-[#243B55] px-4 pt-2 pb-3">

                      {cat.sub_criteria.length === 0 && (
                        <div className="text-[#546E7A] text-sm py-2 italic">No sub-criteria yet.</div>
                      )}

                      {cat.sub_criteria.map(sc => (
                        <div key={sc.id}
                          className="flex items-center gap-3 py-2 border-b border-[#243B55]/50 last:border-0">
                          <div className="flex-1 text-[#B0BEC5] text-sm">
                            <InlineEdit value={sc.name} onSave={name => renameCriterion(sc.id, name)} disabled={!canEdit} />
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <input
                              type="number"
                              min="0" max="100" step="1"
                              defaultValue={Math.round((sc.weight || 0) * 100)}
                              onChange={e => updateWeight(sc.id, e.target.value)}
                              disabled={!canEdit}
                              className="w-12 bg-[#0A1628] text-[#00D4FF] text-center text-xs rounded border border-[#00D4FF]/30 focus:border-[#00D4FF] focus:outline-none py-0.5"
                            />
                            <span className="text-[#546E7A] text-xs">%</span>
                          </div>
                          {canEdit && (
                            <button onClick={() => deleteCriterion(sc.id)}
                              className="text-[#EF5350] hover:text-red-400 flex-shrink-0">
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      ))}

                      {/* Sub-criteria weight total */}
                      {cat.sub_criteria.length > 0 && (
                        <div className={`text-xs pt-2 flex justify-end ${subOk ? 'text-[#00E676]' : 'text-[#FFB300]'}`}>
                          Sub-criteria total: {subTotal}% {subOk ? '✓' : '⚠️'}
                        </div>
                      )}

                      {/* Add sub-criterion */}
                      <div className={canEdit ? 'flex items-center gap-2 pt-3' : 'hidden'}>
                        <input
                          type="text"
                          value={newSubName[cat.id] || ''}
                          onChange={e => setNewSubName(prev => ({ ...prev, [cat.id]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') addSubCriterion(cat.id, cat.position); }}
                          placeholder="Add sub-criterion..."
                          className="flex-1 bg-[#0A1628] text-white px-3 py-1.5 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none text-sm"
                        />
                        <button
                          onClick={() => addSubCriterion(cat.id, cat.position)}
                          className="text-[#00D4FF] hover:text-white border border-[#00D4FF]/40 hover:border-[#00D4FF] rounded-lg p-1.5 transition-colors">
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Add new category */}
            <div className={canEdit ? 'panel-card border border-dashed border-[#546E7A]/40' : 'hidden'}>
              <div className="text-[#B0BEC5] text-sm mb-3 font-semibold">
                Add New Category
              </div>
              {/* Scope toggle */}
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <button
                  onClick={() => setNewCatScope('position')}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    newCatScope === 'position'
                      ? 'bg-[#00D4FF]/15 border-[#00D4FF]/50 text-[#00D4FF]'
                      : 'bg-transparent border-[#546E7A]/30 text-[#546E7A] hover:text-[#B0BEC5]'
                  }`}>
                  {selectedPosition} Only
                </button>
                <button
                  onClick={() => setNewCatScope('all')}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    newCatScope === 'all'
                      ? 'bg-[#00E676]/15 border-[#00E676]/50 text-[#00E676]'
                      : 'bg-transparent border-[#546E7A]/30 text-[#546E7A] hover:text-[#B0BEC5]'
                  }`}>
                  All Positions
                </button>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newCatName}
                  onChange={e => setNewCatName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addCategory(); }}
                  placeholder="Category name..."
                  className="flex-1 min-w-0 bg-[#0A1628] text-white px-3 py-2 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none text-sm"
                />
                <button onClick={addCategory}
                  className="flex items-center gap-1 px-3 py-2 bg-[#00D4FF]/10 border border-[#00D4FF]/40 text-[#00D4FF] rounded-lg text-sm hover:bg-[#00D4FF]/20 transition-colors">
                  <Plus size={14} /> Add
                </button>
              </div>
              <div className="text-[#546E7A] text-xs mt-1">
                {newCatScope === 'all'
                  ? 'This category will appear for all positions.'
                  : `This category will be specific to ${selectedPosition} players.`}
              </div>
            </div>
          </div>
        )}

      <CriteriaMeasurables />

      </div>
    </div>
  );




};

export default Criteria;
